import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import api from "../../api/axios";

import "../../assets/css/Deals/DealsCreate.css";
import { Contact } from "lucide-react";

export default function DealsCreate({
  isOpen,
  onClose,
  lead,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);

  const [form, setForm] = useState({
    lead_id: "",
    lead_amount: "",
    deal_amount: "",
    expiry_date: "",
    po_document: null,
    status: "Active Deal",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log("trying to fetch data")
        const [userRes] = await Promise.all([
          api.get("/users"),
        ]);

        setUsers(userRes.data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (lead) {
      setForm({
        lead_id: lead.id,
        lead_amount: lead.value || "",
        deal_amount: "",
        expiry_date: "",
        po_document: null,
        status: "Active Deal",
      });
    }
  }, [lead]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    // FILE INPUT
    if (files) {
      setForm((prev) => ({
        ...prev,
        [name]: files[0],
      }));
      return;
    }

    // NUMBER FORMATTED FIELDS
    if (name === "deal_amount" || name === "expected_expense") {
      const rawValue = value.replace(/,/g, "").replace(/\D/g, "");

      setForm((prev) => ({
        ...prev,
        [name]: rawValue,
      }));

      return;
    }

    // DEFAULT FIELDS
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ✅ API CALL
  const submit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);


      const payload = {
        title: lead.title,
        organization_id: lead?.organization?.id || 0,
        lead_id: form.lead_id,
        deal_amount: Number(form.deal_amount),
        contact_id: lead?.contact?.id || 0,
        expected_expense: Number(form.expected_expense),
        
        expiry_date: form.expiry_date,
        status: form.status,
        profit: Number(form.deal_amount) - Number(form.expected_expense),
        actual_profit: Number(form.deal_amount) - Number(form.expected_expense),
        assigned_to: form.assign_to,
        expected_close_date: form.expiry_date,

      };


      // 1️⃣ Create Deal
      const dealRes = await api.post("/deal/create", payload, {});
      const createdDeal = dealRes?.data;

      // Optional: Upload PO Document if selected
      if (form.po_document && createdDeal?.id) {
        try {
          const docFormData = new FormData();
          docFormData.append("file", form.po_document);
          docFormData.append("doc_type", "PO");
          docFormData.append("doc_number", form.po_number || "");
          docFormData.append("amount", form.deal_amount || "");
          docFormData.append("stage", form.status || "Active Deal");
          docFormData.append("notes", "Attached upon deal creation");

          await api.post(`/deal/${createdDeal.id}/documents/upload`, docFormData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (uploadErr) {
          console.warn("PO document upload warning:", uploadErr);
        }
      }

      // 2️⃣ Update Lead Status
      await api.put(`/lead/update/${lead.id}`, {
        status: "Deal Confirmation",
        title: lead.title,
      });

      Swal.fire({
        icon: "success",
        title: "Deal Created",
        timer: 1500,
        showConfirmButton: false,
      });

      await onSuccess?.();
      await onClose?.();

    } catch (err) {
      console.error(err);

      Swal.fire({
        icon: "error",
        title: "Failed to create deal",
        text:
          err?.response?.data?.detail ||
          "Something went wrong",
      });

    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>Create Deal</h3>
        </div>

        <form className="modal-body" onSubmit={submit}>
          <div className="form-grid">

            {/* LEAD INFO (READ ONLY) */}
            <div className="form-group">
              <label>Lead Title</label>
              <input
                value={lead?.title || ""}
                disabled
                placeholder="Website Development Lead"
              />
            </div>

            <div className="form-group">
              <label>Organization</label>
              <input
                value={lead?.organization?.name || ""}
                disabled
                placeholder="ABC Pvt Ltd"
              />
            </div>

            {/* LEAD AMOUNT */}
            <div className="form-group">
              <label>Expected Revenue</label>
              <input
                type="text"
                name="lead_amount"
                readOnly
                value={
                  form.lead_amount
                    ? Number(form.lead_amount).toLocaleString("en-IN")
                    : ""
                }
                onChange={handleChange}
                placeholder="50,000"
                required
              />
            </div>

            {/* DEAL AMOUNT */}
            <div className="form-group">
              <label>Actual Revenue</label>
              <input
                type="text"
                name="deal_amount"
                value={
                  form.deal_amount
                    ? Number(form.deal_amount).toLocaleString("en-IN")
                    : ""
                }
                onChange={handleChange}
                placeholder="1,25,000"
                required
              />
            </div>

            {/* EXPECTED EXPENSE */}
            <div className="form-group">
              <label>Expected Expense</label>
              <input
                type="text"
                name="expected_expense"
                value={
                  form.expected_expense
                    ? Number(form.expected_expense).toLocaleString("en-IN")
                    : ""
                }
                onChange={handleChange}
                placeholder="20,000"
                required
              />
            </div>

      

            {/* ASSIGN TO */}
            <div className="form-group">
              <label>Assign To</label>
              <select
                name="assign_to"
                value={form.assign_to}
                onChange={handleChange}
                required
              >
                <option value="">Select User</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>

            {/* EXPIRY DATE */}
            <div className="form-group">
              <label>Expected Close Date</label>
              <input
                type="date"
                name="expiry_date"
                value={form.expiry_date}
                onChange={handleChange}
                required
              />
            </div>

            {/* OPTIONAL PO NUMBER */}
            <div className="form-group">
              <label>PO Number (Optional)</label>
              <input
                type="text"
                name="po_number"
                value={form.po_number || ""}
                onChange={handleChange}
                placeholder="e.g. PO-2026-001"
              />
            </div>

            {/* OPTIONAL PO DOCUMENT ATTACHMENT */}
            <div className="form-group">
              <label>Attach PO Document (Optional)</label>
              <input
                type="file"
                name="po_document"
                onChange={handleChange}
                accept=".pdf,.doc,.docx,.xlsx,.xls,.png,.jpg,.jpeg"
              />
            </div>

          </div>

          <div className="modal-footer">
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? "Creating..." : "Create Deal"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

//just comment

